import React, { useRef, useState } from "react";
import Grid from "@mui/material/Grid2";
import { Button, Chip, MenuItem, Stack, Typography } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../config/design/styles";
import { defaultVariables } from "../../../../config";
import { TableCount } from "../../../../config/Permissions";
import { getDataTableLocalization } from "../../../../config/Translate";
import { tableIcons } from "../../../../config/design/TableIcons";
import { currencySymbol, price, showTableDate } from "../../../../config/values";
import AppAnimate from "../../../components/AppAnimate";
import Title from "../../../components/elements/Title";
import RayaField from "../../../components/elements/fields/RayaField";
import { Download, RefreshCw, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

function EInvoicesPage({ classes }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const tableRef = useRef();
    const [typeFilter, setTypeFilter] = useState("vendor_commission");
    const [statusFilter, setStatusFilter] = useState("all");
    const [busy, setBusy] = useState(false);

    const loginToken = localStorage.getItem("ACT");
    const config = {
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${loginToken}`,
        },
    };

    const reload = () => tableRef.current && tableRef.current.onQueryChange();

    const downloadInvoice = async (inv) => {
        setBusy(true);
        try {
            const file = await axios.get(`${defaultVariables.url}/einvoices/download/${inv.id}`, {
                ...config,
                responseType: "blob",
                params: { format: "pdf" },
            });
            const contentType = file.headers["content-type"] || "";
            const disposition = file.headers["content-disposition"] || "";
            let filename = `${inv.invoice_no || inv.uuid || inv.id}.pdf`;
            const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
            if (match && match[1]) {
                filename = match[1].replace(/['"]/g, "");
            } else if (contentType.includes("xml")) {
                filename = filename.replace(/\.pdf$/i, ".xml");
            } else if (contentType.includes("zip")) {
                filename = filename.replace(/\.pdf$/i, ".zip");
            }
            const url = window.URL.createObjectURL(new Blob([file.data], { type: contentType || "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || t("invoiceDownloadFailed"), { variant: "error" });
        } finally {
            setBusy(false);
        }
    };

    const retryInvoice = async (inv) => {
        setBusy(true);
        try {
            await axios.post(`${defaultVariables.url}/einvoices/retry`, { id: inv.id }, config);
            enqueueSnackbar(t("invoiceRetryQueued"), { variant: "success" });
            reload();
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || t("settlementActionFailed"), { variant: "error" });
        } finally {
            setBusy(false);
        }
    };

    const statusChip = (status) => {
        const colors = {
            sent: "success",
            pending: "default",
            processing: "info",
            failed: "warning",
            dead: "error",
        };
        return <Chip size="small" color={colors[status] || "default"} label={t(`invoiceStatus_${status}`) || status} />;
    };

    const columns = [
        {
            title: t("registerDate"),
            field: "created_at",
            render: (row) => <Typography className={classes.dateTable}>{showTableDate(row.created_at)}</Typography>,
        },
        {
            title: t("orderNumber"),
            field: "order.number",
            render: (row) =>
                row.order?.number ? (
                    <Link to={`/panel/manage/view-order/${row.order.number}`}>{row.order.number}</Link>
                ) : (
                    "-"
                ),
        },
        {
            title: t("invoiceType"),
            field: "type",
            render: (row) => t(`invoiceType_${row.type}`) || row.type,
        },
        {
            title: t("documentType"),
            field: "document_type",
            render: (row) => (row.document_type ? t(`documentType_${row.document_type}`) : "-"),
        },
        {
            title: t("invoiceNo"),
            field: "invoice_no",
        },
        {
            title: t("recipient"),
            field: "recipient_name",
            render: (row) => (
                <div>
                    <div>{row.recipient_name}</div>
                    <Typography variant="caption">{row.recipient_tax_number}</Typography>
                </div>
            ),
        },
        {
            title: t("amount"),
            field: "amount",
            render: (row) => `${price(row.amount)} ${currencySymbol}`,
        },
        {
            title: t("status"),
            field: "status",
            render: (row) => statusChip(row.status),
        },
        {
            title: t("actions"),
            sorting: false,
            render: (row) => (
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download size={14} />}
                        disabled={busy || (!row.xml_path && !row.pdf_path)}
                        onClick={() => downloadInvoice(row)}
                    >
                        {t("download")}
                    </Button>
                    {(row.status === "failed" || row.status === "dead") && (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<RotateCcw size={14} />}
                            disabled={busy}
                            onClick={() => retryInvoice(row)}
                        >
                            {t("retry")}
                        </Button>
                    )}
                </Stack>
            ),
        },
    ];

    return (
        <AppAnimate animation="transition.slideUpIn" delay={200} title={t("eInvoicesTitle")}>
            <Grid container spacing={2}>
                <Grid item size={12} className="flex-space">
                    <div>
                        <Title>{t("eInvoicesTitle")}</Title>
                        <Typography variant="body2" color="text.secondary">
                            {t("eInvoicesDesc")}
                        </Typography>
                    </div>
                    <Button startIcon={<RefreshCw size={16} />} onClick={reload} disabled={busy}>
                        {t("refresh")}
                    </Button>
                </Grid>
                <Grid item size={{ xs: 12, md: 3 }}>
                    <RayaField
                        select
                        fullWidth
                        label={t("invoiceType")}
                        value={typeFilter}
                        onChange={(e) => {
                            setTypeFilter(e.target.value);
                            setTimeout(reload, 0);
                        }}
                    >
                        <MenuItem value="all">{t("all")}</MenuItem>
                        <MenuItem value="vendor_commission">{t("invoiceType_vendor_commission")}</MenuItem>
                    </RayaField>
                </Grid>
                <Grid item size={{ xs: 12, md: 3 }}>
                    <RayaField
                        select
                        fullWidth
                        label={t("status")}
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setTimeout(reload, 0);
                        }}
                    >
                        <MenuItem value="all">{t("all")}</MenuItem>
                        <MenuItem value="pending">{t("invoiceStatus_pending")}</MenuItem>
                        <MenuItem value="sent">{t("invoiceStatus_sent")}</MenuItem>
                        <MenuItem value="failed">{t("invoiceStatus_failed")}</MenuItem>
                        <MenuItem value="dead">{t("invoiceStatus_dead")}</MenuItem>
                    </RayaField>
                </Grid>
                <Grid item size={12}>
                    <MaterialTable
                        tableRef={tableRef}
                        icons={tableIcons}
                        title=""
                        columns={columns}
                        localization={getDataTableLocalization()}
                        options={{
                            pageSize: TableCount,
                            pageSizeOptions: [TableCount, 24, 48],
                            search: true,
                            sorting: true,
                        }}
                        data={(query) =>
                            new Promise((resolve, reject) => {
                                axios
                                    .get(
                                        `${defaultVariables.url}/einvoices/list?page=${query.page + 1}&per_page=${
                                            query.pageSize
                                        }&search=${encodeURIComponent(query.search || "")}&type=${typeFilter}&status=${statusFilter}`,
                                        config
                                    )
                                    .then((res) => {
                                        resolve({
                                            data: res.data.data || [],
                                            page: (res.data.current_page || 1) - 1,
                                            totalCount: res.data.total || 0,
                                        });
                                    })
                                    .catch(reject);
                            })
                        }
                    />
                </Grid>
            </Grid>
        </AppAnimate>
    );
}

export default withStyles(Styles)(EInvoicesPage);
